/**
 * MD-31 ASKLAW-EMBER-THEME Phase 2 - /chat route Gemini-style layout.
 *
 * Per spec F-FE-THEME-GEMINI.md §6.3 + §7.1-7.6 + AC-06..AC-10.
 *
 * Replaces Direction A "Tu phap restraint" single-column shell with:
 *   - 280px Sidebar left rail (drawer on mobile < 1024px)
 *   - 56px TopBar (model selector + theme toggle)
 *   - Main area: GreetingBlock + AccentSparkle (1 instance) + SuggestionChipRow
 *     when no message yet
 *   - Bottom-anchored PillInput sticky max-width 720px
 *   - Message thread bubble (user right amber soft, assistant left paper-elev border)
 *   - Always-on 4-luat disclaimer footer per assistant message (AC-10)
 *
 * Preserves existing internal logic:
 *   - Anon thread localStorage + soft warn + hard cap 20
 *   - Citation marker click pulse + SourcePanel
 *   - askStream SSE + dispatch reducer
 *   - Refusal banner
 *   - AnswerActions feedback
 *   - Library drawer (only when active)
 *
 * GSAP orchestration timeline on mount via useGsapChatOrchestrate (lazy import).
 * Reduced-motion -> skip per AC-14.
 */

import {
  useCallback,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
  type CSSProperties,
} from 'react';
import {
  INITIAL_STATE,
  reduceChat,
  splitByCitations,
  type ChatAction,
  type ChatState,
} from '@core/chat-helpers';
import type { CitationShape, RefusalResponse } from '../../../src/workers/refusal/types';
import { useAuth } from '../use-auth';
import { useLang } from '../use-lang';
import {
  askStream,
  extractGroundedSources,
  extractCitedDocIds,
  applyCitedDocIds,
  type AskHistoryTurn,
  type GroundedSourceDoc,
} from '../lib/chat-api';
import {
  loadAnonThreadList,
  resolveAnonThreadId,
  upsertAnonThread,
  type AnonThread,
  type SidebarMessage,
} from '../components/chat/ChatHistory';
import { LibraryDrawer } from '../components/LibraryDrawer';
import { SourcePanel } from '../components/SourceCard';
import { AnswerActions } from '../components/AnswerActions';
import { Button } from '../components/primitives';
import { AppShell } from '../components/shell/AppShell';
import { Sidebar } from '../components/chat/Sidebar';
import { TopBar } from '../components/chat/TopBar';
import { GreetingBlock } from '../components/chat/GreetingBlock';
import { SuggestionChipRow } from '../components/chat/SuggestionChip';
import { PillInput, type PillInputHandle } from '../components/chat/PillInput';
import { ProcessTimeline } from '../components/chat/ProcessTimeline';
import {
  LawDocPanel,
  panelWidthFor,
  type PanelSize,
  type PanelSizeAction,
} from '../components/chat/LawDocPanel';
import { useGsapChatOrchestrate } from '../lib/use-gsap-chat-orchestrate';
import { trackEvent } from '../lib/analytics';
import '../styles/chat.css';

// ---- Types ----

type ChatMessage = {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  citations?: CitationShape[];
  refusal?: RefusalResponse;
  created_at: string;
};

type AnonThreadStore = {
  messages: ChatMessage[];
  turn_count: number;
  started_at: string;
  last_at: string;
  /** Sidebar thread identity (BUG-2026-06-12 Bug 3): restored on hydrate so a
   * reload-then-continue does NOT mint a new session id (= duplicate sidebar
   * entry with the same title). Optional: legacy stores predate the field. */
  thread_id?: string;
};

// ---- Constants ----

const ANON_HARD_CAP = 20;
const ANON_SOFT_WARN_AT = 18;
const ANON_THREAD_TTL_MS = 7 * 24 * 3600 * 1000;
const ANON_SESSION_COOKIE = 'asklaw_anon_session';
const HISTORY_SLICE = 6;

// ---- Helpers ----

function readCookie(name: string): string | null {
  if (typeof document === 'undefined') return null;
  const match = document.cookie.match(new RegExp('(?:^|; )' + name + '=([^;]+)'));
  return match ? decodeURIComponent(match[1]) : null;
}

function writeCookie(name: string, value: string, days: number): void {
  if (typeof document === 'undefined') return;
  const exp = new Date(Date.now() + days * 24 * 3600 * 1000).toUTCString();
  document.cookie = `${name}=${encodeURIComponent(value)}; expires=${exp}; path=/; SameSite=Lax`;
}

function ensureAnonSessionId(): string {
  let sid = readCookie(ANON_SESSION_COOKIE);
  if (sid) return sid;
  sid = (crypto && 'randomUUID' in crypto)
    ? crypto.randomUUID()
    : `anon-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
  writeCookie(ANON_SESSION_COOKIE, sid, 7);
  return sid;
}

function loadAnonThread(sid: string): AnonThreadStore | null {
  try {
    const raw = window.localStorage.getItem(`asklaw_anon_thread:${sid}`);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as AnonThreadStore;
    const last = Date.parse(parsed.last_at);
    if (!Number.isFinite(last) || Date.now() - last > ANON_THREAD_TTL_MS) {
      window.localStorage.removeItem(`asklaw_anon_thread:${sid}`);
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

function saveAnonThread(sid: string, store: AnonThreadStore): void {
  try {
    window.localStorage.setItem(`asklaw_anon_thread:${sid}`, JSON.stringify(store));
  } catch {
    // localStorage quota or disabled - silent no-op acceptable.
  }
}

function clearAnonThread(sid: string): void {
  try {
    window.localStorage.removeItem(`asklaw_anon_thread:${sid}`);
    window.localStorage.removeItem(`asklaw_anon_warn_dismissed_session:${sid}`);
  } catch {
    // no-op
  }
}

function buildHistorySlice(messages: ChatMessage[]): AskHistoryTurn[] {
  const recent = messages.slice(-HISTORY_SLICE);
  return recent.map((m) => ({
    role: m.role,
    content: m.content.length > 500 ? m.content.slice(0, 500) : m.content,
  }));
}

function randomId(): string {
  return (crypto && 'randomUUID' in crypto)
    ? crypto.randomUUID()
    : `msg-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

function refusalBannerClass(refusal: RefusalResponse): string {
  const color = (refusal as { banner_color?: string }).banner_color;
  if (color === 'red') return 'asklaw-chat__refusal asklaw-chat__refusal--red';
  if (color === 'amber') return 'asklaw-chat__refusal asklaw-chat__refusal--amber';
  return 'asklaw-chat__refusal asklaw-chat__refusal--gray';
}

// ---- Subcomponents ----

function AssistantMessageBody({
  message,
  lang,
  onCiteClick,
}: {
  message: ChatMessage;
  lang: 'vi' | 'en';
  onCiteClick: (cid: string) => void;
}) {
  const markers = useMemo(
    () =>
      (message.citations ?? []).map((c) => ({
        cid: c.cid,
        span_start: c.span_start,
        span_end: c.span_end,
      })),
    [message.citations]
  );
  const segments = useMemo(
    () => splitByCitations(message.content, markers),
    [message.content, markers]
  );
  return (
    <span>
      {segments.map((seg, i) =>
        seg.kind === 'cite' ? (
          <mark
            key={i}
            className="asklaw-chat__cite-marker"
            data-cid={seg.cid}
            onClick={() => onCiteClick(seg.cid)}
            tabIndex={0}
            role="button"
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onCiteClick(seg.cid);
              }
            }}
            aria-label={
              lang === 'vi'
                ? `Xem trích dẫn ${seg.text}`
                : `View citation ${seg.text}`
            }
          >
            {seg.text}
          </mark>
        ) : (
          <span key={i}>{seg.text}</span>
        )
      )}
    </span>
  );
}

function AnonSoftWarn({
  count,
  lang,
  onDismiss,
}: {
  count: number;
  lang: 'vi' | 'en';
  onDismiss: () => void;
}) {
  return (
    <div className="asklaw-chat__anon-warn" role="status" aria-live="polite">
      <span className="asklaw-chat__anon-warn-text">
        {lang === 'vi'
          ? `Bạn đã hỏi ${count}/${ANON_HARD_CAP} lượt - đăng nhập để chat không giới hạn + lưu lịch sử.`
          : `${count}/${ANON_HARD_CAP} free questions used - sign in for unlimited + saved history.`}
      </span>
      <a className="asklaw-chat__anon-warn-cta" href="/auth/login">
        {lang === 'vi' ? 'Đăng nhập Google' : 'Sign in with Google'}
      </a>
      <button
        type="button"
        className="asklaw-chat__anon-warn-dismiss"
        onClick={onDismiss}
        aria-label={lang === 'vi' ? 'Đóng thông báo' : 'Dismiss'}
      >
        <span aria-hidden="true">x</span>
      </button>
    </div>
  );
}

function FourLawDisclaimer({ lang }: { lang: 'vi' | 'en' }) {
  // legal-upl C1-C4 + DECISIONS s2026-06-12-CHAT-DISCLAIMER-CONDENSE-INFO-POPOVER:
  // ONE persistent slim bar anchored to the fixed composer (NOT per-message),
  // always visible in BOTH the empty greeting state AND an active thread, never
  // collapsed/dismissible/scroll-away (C1). The always-visible line carries the
  // two highest-risk locked tokens: the Luat AI 2026 transparency lead ("AI tham
  // khảo" / "AI reference", C4) + the UPL clause (chat-context noun "Câu trả
  // lời", NOT "Bài viết"). The FULL locked 4-law wording (verbatim, C2) stays in
  // the DOM inside the (i) popover - hidden attr when closed, never removed.
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  // Close the popover on outside click / ESC while open.
  useEffect(() => {
    if (!open) return;
    const onDocDown = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDocDown);
    window.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocDown);
      window.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div className="chat-disclaimer-bar" role="note" ref={rootRef}>
      {lang === 'vi' ? (
        <>AI tham khảo - Câu trả lời không tạo lập quan hệ luật sư - khách hàng.</>
      ) : (
        <>AI reference - This response does NOT establish a lawyer-client relationship.</>
      )}
      <button
        type="button"
        className="chat-disclaimer-bar__info"
        aria-expanded={open}
        aria-controls="chat-disclaimer-full"
        aria-label={
          lang === 'vi' ? 'Xem đầy đủ căn cứ pháp lý' : 'View full legal basis'
        }
        onClick={() => setOpen((v) => !v)}
      >
        <span aria-hidden="true">i</span>
      </button>
      <div
        id="chat-disclaimer-full"
        className="chat-disclaimer-bar__popover"
        role="note"
        hidden={!open}
      >
        {lang === 'vi' ? (
          <>
            AI tham khảo theo Luật Luật sư 65/2006/QH11 + 20/2012/QH13, Luật AI 2026,
            Luật Bảo vệ dữ liệu cá nhân 2026. Kiểm tra tại vbpl.moj.gov.vn. Câu trả lời
            không tạo lập quan hệ luật sư - khách hàng.
          </>
        ) : (
          <>
            AI reference per Law on Lawyers 65/2006/QH11 + 20/2012/QH13, AI Law 2026,
            Personal Data Protection Law 2026. Verify at vbpl.moj.gov.vn. This response
            does NOT establish a lawyer-client relationship.
          </>
        )}
      </div>
    </div>
  );
}

// ---- Main route ----

function chatReducer(state: ChatState, action: ChatAction): ChatState {
  return reduceChat(state, action);
}

export function ChatRoute() {
  const { lang } = useLang();
  const { isAuthenticated, accessToken, profile } = useAuth();
  const [streamState, dispatch] = useReducer(chatReducer, INITIAL_STATE);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  // Grounded sources of the LATEST answer (Ask Grounded RAG `sources` SSE event).
  // The chat reducer IGNORES `sources` (returns state); we capture it here so the
  // right-side LawDocPanel reflects the most recent answer - mirrors the /ask
  // route's "latest grounded answer" model.
  const [latestSources, setLatestSources] = useState<GroundedSourceDoc[]>([]);
  // Flicker fix (BUG-2026-06-15 ADDENDUM): buffer the retrieval set from the
  // `sources` event WITHOUT rendering it. The panel is revealed only after the
  // post-stream `cited` meta resolves (applyCitedDocIds) - so a no-info answer
  // never flashes its noise docs during the ~12-16s answer stream, and a grounded
  // answer reveals its sources (cited-marked) once it completes.
  const pendingSourcesRef = useRef<GroundedSourceDoc[]>([]);
  // LawDocPanel desktop width state (single source of truth here so the
  // .chat-layout grid 3rd column + the fixed panel resize in lock-step). The
  // panel requests changes via onSizeChange; 'restore' un-minimizes to the size
  // before minimizing (tracked in prevSizeRef). See LawDocPanel.panelWidthFor.
  const [panelSize, setPanelSize] = useState<PanelSize>('normal');
  const prevPanelSizeRef = useRef<Exclude<PanelSize, 'minimized'>>('normal');
  const changePanelSize = useCallback((action: PanelSizeAction) => {
    setPanelSize((cur) => {
      if (action === 'restore') return prevPanelSizeRef.current;
      if (action === 'minimized' && cur !== 'minimized') {
        prevPanelSizeRef.current = cur === 'expanded' ? 'expanded' : 'normal';
      }
      return action;
    });
  }, []);
  const [warnDismissed, setWarnDismissed] = useState(false);
  const [activePulseCid, setActivePulseCid] = useState<string | null>(null);
  const [currentSessionId, setCurrentSessionId] = useState<string | null>(null);
  const [sidebarRefreshTick, setSidebarRefreshTick] = useState(0);
  const [libraryOpen, setLibraryOpen] = useState(false);
  const [libraryRefreshTick, setLibraryRefreshTick] = useState(0);
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const pillInputRef = useRef<PillInputHandle>(null);
  const abortRef = useRef<AbortController | null>(null);
  const shellRef = useRef<HTMLDivElement>(null);

  const anonSessionId = useMemo(
    () => (isAuthenticated ? null : ensureAnonSessionId()),
    [isAuthenticated]
  );

  // GSAP orchestrate on mount when no message yet (greeting shown).
  useGsapChatOrchestrate(shellRef, { skip: messages.length > 0 });

  // Hydrate anon thread on mount.
  useEffect(() => {
    if (!anonSessionId) return;
    const stored = loadAnonThread(anonSessionId);
    if (stored) {
      setMessages(stored.messages);
      // BUG-2026-06-12 Bug 3: restore the sidebar thread identity together with
      // the messages. Without it the next send minted a NEW session id and the
      // same conversation appeared TWICE in history. Legacy stores lack
      // thread_id -> resolveAnonThreadId falls back to matching the first
      // message id against the saved thread list.
      const restoredId = resolveAnonThreadId(
        stored.thread_id,
        stored.messages[0]?.id,
        loadAnonThreadList()
      );
      if (restoredId) setCurrentSessionId(restoredId);
    }
    try {
      const dismissed = window.localStorage.getItem(
        `asklaw_anon_warn_dismissed_session:${anonSessionId}`
      );
      setWarnDismissed(dismissed === '1');
    } catch {
      // no-op
    }
  }, [anonSessionId]);

  // Persist anon thread on messages change.
  useEffect(() => {
    if (!anonSessionId) return;
    if (messages.length === 0) return;
    const last = messages[messages.length - 1];
    const turn_count = messages.filter((m) => m.role === 'user').length;
    const started_at = messages[0].created_at;
    saveAnonThread(anonSessionId, {
      messages,
      turn_count,
      started_at,
      last_at: last.created_at,
      // Keep the sidebar thread identity with the snapshot (BUG-2026-06-12).
      thread_id: currentSessionId ?? undefined,
    });
  }, [anonSessionId, messages, currentSessionId]);

  const anonTurnCount = useMemo(
    () => (anonSessionId ? messages.filter((m) => m.role === 'user').length : 0),
    [anonSessionId, messages]
  );

  const showSoftWarn =
    Boolean(anonSessionId) &&
    !warnDismissed &&
    anonTurnCount >= ANON_SOFT_WARN_AT &&
    anonTurnCount < ANON_HARD_CAP;

  const atHardCap = Boolean(anonSessionId) && anonTurnCount >= ANON_HARD_CAP;

  const insertIntoInput = useCallback((text: string) => {
    pillInputRef.current?.setValue(text);
    pillInputRef.current?.focus();
  }, []);

  const handleCiteClick = useCallback((cid: string) => {
    setActivePulseCid(cid);
    window.setTimeout(() => setActivePulseCid(null), 2000);
  }, []);

  const dismissWarn = useCallback(() => {
    setWarnDismissed(true);
    if (anonSessionId) {
      try {
        window.localStorage.setItem(
          `asklaw_anon_warn_dismissed_session:${anonSessionId}`,
          '1'
        );
      } catch {
        // no-op
      }
    }
  }, [anonSessionId]);

  const resetThread = useCallback(() => {
    if (abortRef.current) abortRef.current.abort();
    setMessages([]);
    setLatestSources([]);
    pendingSourcesRef.current = [];
    setWarnDismissed(false);
    setActivePulseCid(null);
    setCurrentSessionId(null);
    pillInputRef.current?.clear();
    dispatch({ kind: 'sse_close' });
    if (anonSessionId) {
      clearAnonThread(anonSessionId);
      const fresh = (crypto && 'randomUUID' in crypto)
        ? crypto.randomUUID()
        : `anon-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
      writeCookie(ANON_SESSION_COOKIE, fresh, 7);
    }
  }, [anonSessionId]);

  const stopGeneration = useCallback(() => {
    if (abortRef.current) {
      abortRef.current.abort();
      abortRef.current = null;
    }
    dispatch({ kind: 'sse_close' });
  }, []);

  // Load a thread when user clicks a sidebar item.
  const handleSelectSession = useCallback(
    (sid: string, loaded: SidebarMessage[]) => {
      if (abortRef.current) abortRef.current.abort();
      setActivePulseCid(null);
      // Loaded threads have no live `sources` event -> clear the grounded panel.
      setLatestSources([]);
      pendingSourcesRef.current = [];
      dispatch({ kind: 'sse_close' });
      setCurrentSessionId(sid);
      const normalized: ChatMessage[] = loaded.map((m) => ({
        id: m.id,
        role: m.role,
        content: m.content,
        citations: Array.isArray(m.citations) ? (m.citations as CitationShape[]) : undefined,
        created_at: m.created_at,
      }));
      setMessages(normalized);
    },
    []
  );

  const persistMessage = useCallback(
    async (
      sessionId: string,
      role: 'user' | 'assistant',
      content: string,
      citations?: CitationShape[]
    ) => {
      if (isAuthenticated && accessToken) {
        try {
          await fetch('/api/v1/me/chat/message', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${accessToken}`,
            },
            body: JSON.stringify({ session_id: sessionId, role, content, citations }),
          });
        } catch (err) {
          // eslint-disable-next-line no-console
          console.error('[chat] persist message error', err);
        }
      }
    },
    [isAuthenticated, accessToken]
  );

  const upsertAnonSnapshot = useCallback(
    (sid: string, msgs: ChatMessage[]) => {
      const firstUser = msgs.find((m) => m.role === 'user');
      const titleSrc = firstUser?.content ?? (lang === 'vi' ? 'Cuộc trò chuyện' : 'Conversation');
      const title = titleSrc.length > 60 ? titleSrc.slice(0, 57) + '...' : titleSrc;
      const last = msgs[msgs.length - 1];
      const thread: AnonThread = {
        id: sid,
        title,
        created_at: msgs[0]?.created_at ?? new Date().toISOString(),
        last_message_at: last?.created_at ?? new Date().toISOString(),
        messages: msgs.map((m) => ({
          id: m.id,
          role: m.role,
          content: m.content,
          citations: m.citations,
          created_at: m.created_at,
        })),
      };
      upsertAnonThread(thread);
      setSidebarRefreshTick((n) => n + 1);
    },
    [lang]
  );

  const handlePillSubmit = useCallback(
    async (text: string) => {
      if (!text) return;
      if (atHardCap) return;

      // Clear the previous answer's grounded sources so the LawDocPanel does not
      // show stale docs while the new question is being processed.
      setLatestSources([]);
      pendingSourcesRef.current = [];

      const userMsg: ChatMessage = {
        id: randomId(),
        role: 'user',
        content: text,
        created_at: new Date().toISOString(),
      };
      setMessages((prev) => [...prev, userMsg]);
      pillInputRef.current?.clear();

      dispatch({ kind: 'submit', query: text });
      // GA4 key event (PDPA-gated, NO question text sent).
      trackEvent('ask_submitted', { authed: isAuthenticated });

      let activeSessionId = currentSessionId;
      if (!activeSessionId) {
        if (isAuthenticated && accessToken) {
          try {
            const res = await fetch('/api/v1/me/chat/new', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${accessToken}`,
              },
              body: JSON.stringify({ first_message: text }),
            });
            if (res.ok) {
              const data = (await res.json()) as { session_id: string };
              activeSessionId = data.session_id;
            }
          } catch (err) {
            // eslint-disable-next-line no-console
            console.error('[chat] session create error', err);
          }
        } else {
          activeSessionId =
            (crypto && 'randomUUID' in crypto)
              ? crypto.randomUUID()
              : `anon-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
        }
        if (activeSessionId) setCurrentSessionId(activeSessionId);
      }
      if (activeSessionId) {
        if (isAuthenticated) {
          void persistMessage(activeSessionId, 'user', text);
        } else {
          upsertAnonSnapshot(activeSessionId, [...messages, userMsg]);
        }
      }

      const history = buildHistorySlice(messages);

      const accumulator: {
        content: string;
        citations: CitationShape[];
        refusal?: RefusalResponse;
      } = { content: '', citations: [] };

      abortRef.current = new AbortController();
      await askStream({
        query: text,
        session_id: anonSessionId ?? undefined,
        lang,
        history,
        isAuthed: isAuthenticated,
        accessToken: accessToken ?? undefined,
        signal: abortRef.current.signal,
        onEvent: (ev) => {
          dispatch({ kind: 'sse_event', event: ev });
          if (ev.type === 'token') {
            accumulator.content += ev.text;
          } else if (ev.type === 'citation_meta') {
            accumulator.citations.push({
              cid: ev.cid,
              span_start: 0,
              span_end: 0,
              ref: ev.ref,
            });
          } else if (ev.type === 'sources') {
            // Flicker fix: BUFFER the retrieval set; do NOT render the panel yet.
            // It is revealed only after the `cited` meta resolves (below), so a
            // no-info answer never flashes its noise docs mid-stream.
            pendingSourcesRef.current = extractGroundedSources(ev) ?? [];
          } else if (ev.type === 'meta') {
            // Phan 2B (BUG-2026-06-15): the post-stream cited meta tells us WHICH
            // buffered sources the answer actually referenced. applyCitedDocIds
            // marks those rows `cited` (panel badges + groups them) - OR, when the
            // answer cited NOTHING (cited empty, e.g. a "no relevant data" fallback
            // with only keyword-noise retrieval), returns [] so the panel stays
            // hidden (founder decision 2026-06-15). This is the FIRST time the panel
            // is revealed for a live answer. Ignore the citation_masked audit meta
            // (extractCitedDocIds returns null for it).
            const citedIds = extractCitedDocIds(ev);
            if (citedIds) {
              setLatestSources(applyCitedDocIds(pendingSourcesRef.current, citedIds));
            }
          } else if (ev.type === 'refusal') {
            accumulator.refusal = ev.payload;
          } else if (ev.type === 'done') {
            const assistantMsg: ChatMessage = {
              id: randomId(),
              role: 'assistant',
              content: accumulator.content,
              citations:
                accumulator.citations.length > 0 ? accumulator.citations : undefined,
              refusal: accumulator.refusal,
              created_at: new Date().toISOString(),
            };
            setMessages((prev) => {
              const next = [...prev, assistantMsg];
              if (activeSessionId) {
                if (isAuthenticated) {
                  void persistMessage(
                    activeSessionId,
                    'assistant',
                    assistantMsg.content,
                    assistantMsg.citations
                  );
                  setSidebarRefreshTick((n) => n + 1);
                } else {
                  upsertAnonSnapshot(activeSessionId, next);
                }
              }
              return next;
            });
          }
        },
        onError: (err) => {
          // eslint-disable-next-line no-console
          console.error('[chat] ask stream error', err);
          dispatch({
            kind: 'sse_event',
            event: {
              type: 'error',
              code: 'ASKLAW_GEMINI_UNAVAILABLE',
              message: err.message,
            },
          });
        },
        onClose: () => {
          dispatch({ kind: 'sse_close' });
        },
      });
    },
    [
      anonSessionId,
      atHardCap,
      isAuthenticated,
      accessToken,
      lang,
      messages,
      currentSessionId,
      persistMessage,
      upsertAnonSnapshot,
    ]
  );

  // Abort in-flight on unmount.
  useEffect(() => {
    return () => {
      if (abortRef.current) abortRef.current.abort();
    };
  }, []);

  // Prefill composer from `?q=` (entry from news cards / shortcut CTA).
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const params = new URLSearchParams(window.location.search);
    const q = params.get('q');
    if (!q) return;
    insertIntoInput(q);
    params.delete('q');
    params.delete('context_news');
    const next = params.toString();
    const url = window.location.pathname + (next ? `?${next}` : '') + window.location.hash;
    window.history.replaceState(null, '', url);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isStreaming =
    streamState.kind === 'SENDING' || streamState.kind === 'STREAMING';
  const composerDisabled = isStreaming || atHardCap;

  const placeholder = atHardCap
    ? lang === 'vi'
      ? 'Đã đạt giới hạn 20 lượt. Đăng nhập để tiếp tục.'
      : 'Reached 20-question limit. Sign in to continue.'
    : lang === 'vi'
    ? 'Nhập câu hỏi pháp lý của bạn...'
    : 'Ask your legal question...';

  // Phase 1B (spec 2026-06-15 muc 13.4 B7): hint "Enter de gui" + anon counter
  // KHONG con hien duoi composer. Anon counter chuyen ra Sidebar (B6). Keyboard
  // behavior giu nguyen (textarea van xu ly Enter/Shift+Enter). PillInput nhan
  // hint=undefined -> khong render dong hint.

  const ariaLabel =
    lang === 'vi' ? 'Câu hỏi pháp lý của bạn' : 'Your legal question';

  const userName = profile?.name ?? null;

  const showGreeting = messages.length === 0 && streamState.kind === 'IDLE';

  // Phase 1B (muc 13.4 B2): TopBar hien ten hoi thoai + so luot hoi.
  const userTurnCount = useMemo(
    () => messages.filter((m) => m.role === 'user').length,
    [messages]
  );
  const threadTitle = useMemo(() => {
    const firstUser = messages.find((m) => m.role === 'user');
    if (!firstUser) return '';
    const t = firstUser.content.trim();
    return t.length > 60 ? `${t.slice(0, 57)}...` : t;
  }, [messages]);
  const threadSubtitle =
    userTurnCount > 0
      ? lang === 'vi'
        ? `${userTurnCount} lượt hỏi`
        : `${userTurnCount} question${userTurnCount > 1 ? 's' : ''}`
      : undefined;

  const hasGroundedPanel = latestSources.length > 0;

  return (
    <AppShell>
    {/* AppShell wrap (spec 2026-06-12): noi dung ben trong giu nguyen indent
        de minimal diff - chat-layout grid khong doi cau truc. */}
    <div
      className={
        hasGroundedPanel ? 'chat-layout chat-layout--has-panel' : 'chat-layout'
      }
      ref={shellRef}
      style={{ '--lawdoc-w': panelWidthFor(panelSize) } as CSSProperties}
    >
      <Sidebar
        lang={lang}
        isAuthed={isAuthenticated}
        accessToken={accessToken}
        currentSessionId={currentSessionId}
        refreshTick={sidebarRefreshTick}
        variant="rail"
        onNewChat={resetThread}
        onSelectSession={handleSelectSession}
        isAnon={Boolean(anonSessionId)}
        anonTurnCount={anonTurnCount}
        anonCap={ANON_HARD_CAP}
      />
      <Sidebar
        lang={lang}
        isAuthed={isAuthenticated}
        accessToken={accessToken}
        currentSessionId={currentSessionId}
        refreshTick={sidebarRefreshTick}
        variant="drawer"
        isOpen={mobileSidebarOpen}
        onClose={() => setMobileSidebarOpen(false)}
        isAnon={Boolean(anonSessionId)}
        anonTurnCount={anonTurnCount}
        anonCap={ANON_HARD_CAP}
        onNewChat={() => {
          setMobileSidebarOpen(false);
          resetThread();
        }}
        onSelectSession={(sid, msgs) => {
          setMobileSidebarOpen(false);
          handleSelectSession(sid, msgs);
        }}
      />
      <LibraryDrawer
        lang={lang}
        isOpen={libraryOpen}
        onClose={() => setLibraryOpen(false)}
        refreshTick={libraryRefreshTick}
      />
      <div className="chat-layout__main">
        <TopBar
          lang={lang}
          onToggleSidebar={() => setMobileSidebarOpen((v) => !v)}
          showMobileToggle
          onClearChat={messages.length > 0 ? resetThread : undefined}
          title={threadTitle}
          subtitle={threadSubtitle}
        />
        <a className="asklaw-chat__skip" href="#chat-pill-input">
          {lang === 'vi' ? 'Bỏ qua tới ô soạn câu hỏi' : 'Skip to composer'}
        </a>
        <main
          className="chat-main"
          aria-label={lang === 'vi' ? 'Chat Asklaw' : 'Asklaw chat'}
        >
          {showGreeting ? (
            <div className="chat-main__center">
              <GreetingBlock userName={userName} lang={lang} />
              <SuggestionChipRow lang={lang} onPick={insertIntoInput} />
            </div>
          ) : (
            <div
              className="chat-thread"
              role="log"
              aria-live="polite"
            >
              {messages.map((m) => (
                <div
                  key={m.id}
                  className={
                    m.role === 'user'
                      ? 'chat-message chat-message--user'
                      : 'chat-message chat-message--assistant'
                  }
                >
                  <div className="chat-message__bubble" role="article">
                    {m.role === 'assistant' ? (
                      <AssistantMessageBody
                        message={m}
                        lang={lang}
                        onCiteClick={handleCiteClick}
                      />
                    ) : (
                      <span>{m.content}</span>
                    )}
                  </div>
                  {m.role === 'assistant' && m.refusal && (
                    <div className={refusalBannerClass(m.refusal)} role="alert">
                      <strong>
                        {(m.refusal as { message_vn?: string; message_en?: string })
                          .message_vn ||
                          (m.refusal as { message_en?: string }).message_en}
                      </strong>
                    </div>
                  )}
                  {m.role === 'assistant' && !m.refusal && m.content && (
                    <AnswerActions
                      lang={lang}
                      messageId={m.id}
                      messageContent={m.content}
                      accessToken={accessToken}
                    />
                  )}
                  {m.role === 'assistant' && m.citations && m.citations.length > 0 && (
                    <SourcePanel
                      citations={m.citations}
                      assistantContent={m.content}
                      lang={lang}
                      activeCidPulse={activePulseCid}
                      onLibraryChange={() => setLibraryRefreshTick((t) => t + 1)}
                    />
                  )}
                  {/* Issue 3 (legal-upl C1): the 4-luat disclaimer is NO LONGER
                      per-message. It now renders ONCE as a persistent slim bar on
                      the fixed composer (see #chat-pill-input block below) so it
                      is always visible and never scrolls away. */}
                </div>
              ))}

              {(streamState.kind === 'SENDING' ||
                streamState.kind === 'STREAMING') && (
                <div className="chat-message chat-message--assistant">
                  {/* Lawyer-voice progress checklist - replaces the old gold
                      thinking bubble. SENDING -> empty steps -> generic spinner;
                      STREAMING -> live progress from reducer (collapses to
                      summary once the terminal step-4-done frame lands). */}
                  <ProcessTimeline
                    steps={
                      streamState.kind === 'STREAMING'
                        ? streamState.progress ?? []
                        : []
                    }
                    lang={lang}
                  />
                  {/* Streaming answer text bubble below the timeline. */}
                  {streamState.kind === 'STREAMING' && streamState.buffer && (
                    <div
                      className="chat-message__bubble"
                      aria-busy="true"
                      aria-live="polite"
                    >
                      {streamState.buffer}
                      <span className="asklaw-chat__caret-blink" aria-hidden="true">
                        |
                      </span>
                    </div>
                  )}
                </div>
              )}

              {streamState.kind === 'ERROR_RENDER' && (
                <div
                  className="asklaw-chat__refusal asklaw-chat__refusal--red"
                  role="alert"
                >
                  <strong>
                    {lang === 'vi' ? 'Có lỗi: ' : 'Error: '}
                    {streamState.message}
                  </strong>
                  <Button
                    type="button"
                    variant="secondary"
                    size="sm"
                    onClick={() => dispatch({ kind: 'retry' })}
                    className="asklaw-chat__retry"
                  >
                    {lang === 'vi' ? 'Thử lại' : 'Retry'}
                  </Button>
                </div>
              )}

              {showSoftWarn && (
                <AnonSoftWarn count={anonTurnCount} lang={lang} onDismiss={dismissWarn} />
              )}
            </div>
          )}

          {/* Phase 1B (spec 2026-06-15 muc 13.4 B5): composer-dock gom input +
              disclaimer vao 1 khoi kinh bo goc -> het cam giac "3 box roi".
              hint=undefined (B7): khong render dong keyboard-hint + anon counter
              (counter da chuyen ra Sidebar). */}
          <div id="chat-pill-input">
            <div className="composer-dock">
              <PillInput
                ref={pillInputRef}
                placeholder={placeholder}
                ariaLabel={ariaLabel}
                disabled={composerDisabled}
                isStreaming={isStreaming}
                onSubmit={handlePillSubmit}
                onStop={stopGeneration}
              />
              {/* Issue 3 (legal-upl C1-C4): ONE persistent slim disclaimer bar,
                  part of the sticky composer region so it is ALWAYS visible (empty
                  greeting + active thread, mobile + desktop) and never scrolls
                  away. Phase 1B: chi doi style vo (faint, trong dock) - KHONG doi
                  wording/DOM. Drift = HARD VETO #4. */}
              <FourLawDisclaimer lang={lang} />
            </div>
          </div>
        </main>
      </div>
    </div>
    {/* Grounded legal-doc panel. position:fixed self-docking (380px right rail
        >=1200px, bottom-sheet below). MUST render OUTSIDE .chat-layout__main:
        main co backdrop-filter -> tro thanh containing block cho fixed descendant
        -> panel se dock theo main (bi clip + de noi dung) thay vi viewport. Day
        la sibling cua .chat-layout (trong AppShell body, khong co filter/transform
        ancestor) nen position:fixed bam viewport dung. Bug fix Phase 1B-2 (review
        workflow css-layout HIGH). Returns null khi latestSources empty. */}
    <LawDocPanel
      docs={latestSources}
      lang={lang}
      size={panelSize}
      onSizeChange={changePanelSize}
    />
    </AppShell>
  );
}

export default ChatRoute;
